import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslations } from '@/i18n';
import { format } from 'date-fns';
import type { DateGap } from '@/lib/city-date-validation';

interface IncompleteDateCoverageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  gaps: DateGap[];
}

export default function IncompleteDateCoverageDialog({
  isOpen,
  onClose,
  gaps,
}: IncompleteDateCoverageDialogProps) {
  const tValidation = useTranslations('trips.dateCoverage');

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const formatDateRange = (gap: DateGap): string => {
    const startStr = format(gap.start, 'dd/MM/yyyy');
    const endStr = format(gap.end, 'dd/MM/yyyy');
    if (startStr === endStr) {
      return startStr;
    }
    return `${startStr} - ${endStr}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-[30rem] gap-0 rounded-3xl border-0 p-0"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">{tValidation('title')}</DialogTitle>

        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="text-[18px] leading-7 font-bold text-black">{tValidation('title')}</h2>
          <Button
            onClick={onClose}
            variant="unstyled"
            size="none"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f1f1f1] transition-colors hover:bg-[#e4e4e4]"
            aria-label={tValidation('close')}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        </div>

        <div className="px-6 pb-4">
          <p className="mb-4 text-base leading-6 text-[#292929]">{tValidation('description')}</p>

          <div className="flex flex-col gap-3">
            {gaps.map((gap, index) => (
              <div
                key={index}
                className="flex items-center gap-3 rounded-2xl border border-[#f59e0b] bg-[#fffbeb] px-4 py-3"
              >
                <img src="/assets/icons/calendar.svg" alt="" className="h-5 w-5 opacity-60" />
                <span className="text-[15px] leading-6 text-[#92400e]">
                  {tValidation('uncoveredDates', { dates: formatDateRange(gap) })}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 pb-6">
          <Button
            type="button"
            onClick={onClose}
            className="h-auto rounded-full bg-[#242424] px-4 py-3 text-[15px] font-medium text-white transition-colors hover:bg-[#1a1a1a]"
          >
            {tValidation('close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
